/**
 * Extracts the log text covered by a DOM selection, one log line per row.
 *
 * Only the `textSelector` part of each row is kept, so gutter columns
 * (line number, timestamp, stream) never end up in the copied text.
 * Rows cut by the selection boundaries contribute only their selected part.
 * Returns null when the selection does not touch any row of the container.
 */
export function extractSelectedLogText(
  selection: Selection | null,
  container: HTMLElement,
  rowSelector = '.log-line',
  textSelector = '.text',
): string | null {
  if (!selection || selection.isCollapsed || selection.rangeCount === 0) return null;
  const range = selection.getRangeAt(0);
  if (!range.intersectsNode(container)) return null;

  const lines: string[] = [];
  container.querySelectorAll<HTMLElement>(rowSelector).forEach((row) => {
    const text = row.querySelector<HTMLElement>(textSelector);
    if (!text || !range.intersectsNode(text)) return;
    lines.push(clipToRange(text, range).toString());
  });

  return lines.length > 0 ? lines.join('\n') : null;
}

// Range covering the contents of `el`, narrowed to `range` at both ends
function clipToRange(el: HTMLElement, range: Range): Range {
  const clipped = document.createRange();
  clipped.selectNodeContents(el);
  if (range.compareBoundaryPoints(Range.START_TO_START, clipped) > 0) {
    clipped.setStart(range.startContainer, range.startOffset);
  }
  if (range.compareBoundaryPoints(Range.END_TO_END, clipped) < 0) {
    clipped.setEnd(range.endContainer, range.endOffset);
  }
  return clipped;
}

/** True when the user has a non-empty text selection (e.g. after a drag). */
export function hasTextSelection(selection: Selection | null): boolean {
  return !!selection && !selection.isCollapsed && selection.toString().length > 0;
}
